import Image from 'next/image';
import { useState, useEffect } from 'react';
import Masonry from 'react-masonry-css';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Gallery = ({ images = [] }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleClick = (index) => setSelectedImageIndex(index);
  const handleClose = () => setSelectedImageIndex(null);
  const handlePrev = () =>
    setSelectedImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  const handleNext = () =>
    setSelectedImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));

  const breakpointColumnsObj = {
    default: 4,
    1024: 4,
    768: 2,
    0: 2, // mobile: 2 columns
  };

  return (
    <section className="bg-background py-[100px]">
      <div className="gallery-container mx-auto max-w-[1140px] px-4">
        <div className="text-center mb-8">
          <h2 className="text-4xl text-left tracking-[6px] uppercase text-primary font-light mb-4">
            Our Gallery
          </h2>
          <p className="text-xl capitalize font-light text-left text-darkBackground font-gilda">
            Explore some of the work we've done for our clients
          </p>
        </div>

        {/* Masonry layout with react-masonry-css */}
        {mounted && (
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className="flex gap-2"
          columnClassName="flex flex-col gap-2"
        >
          {images.map((image, index) => (
            <div
              key={index}
              className="cursor-pointer overflow-hidden group"
              onClick={() => handleClick(index)}
            >
              <Image
                src={image.src}
                alt={image.alt}
                title={image.alt}
                width={600}
                height={800}
                sizes="(max-width: 768px) 50vw, (max-width: 1024px) 50vw, 25vw"
                className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-500"
                quality={45}
                loading="lazy"
                fetchPriority="low"
              />
            </div>
          ))}
        </Masonry>
        )}

        {/* Modal for Image Slideshow */}
        {selectedImageIndex !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 px-4">
            <div className="relative">
              <button
                aria-label="close gallery slideshow"
                className="absolute top-0 right-4 text-black text-4xl"
                onClick={handleClose}
              >
                &times;
              </button>

              <button
                aria-label="move to previous image in slideshow"
                className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-white bg-opacity-50 p-2 rounded-full"
                onClick={handlePrev}
              >
                <ChevronLeft className="text-black w-6 h-6 hover:text-primary" />
              </button>

              <Image
                src={images[selectedImageIndex].src}
                alt={images[selectedImageIndex].alt}
                title={images[selectedImageIndex].alt}
                width={1000}
                height={1000}
                className="object-contain max-h-[90vh]"
              />

              <button
                aria-label="move to next image in slideshow"
                className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-white bg-opacity-50 p-2 rounded-full"
                onClick={handleNext}
              >
                <ChevronRight className="text-black w-6 h-6 hover:text-primary" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
